import React from "react";
import { motion } from "framer-motion";
import { IMG } from "./siteData";
import { useAdminImages, ChangeBtn } from "./AboutPremium";

const EASE = [0.22, 1, 0.36, 1];
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const FOIL = "linear-gradient(100deg,#8B6B3A 0%,#E9D3A1 25%,#B89B72 45%,#F4E4BC 60%,#8B6B3A 100%)";

const PILLARS = [
  { t: "The", e: "Company", d: "A specialist textile house born in Surat, printing and tailoring premium backdrops and fabrics for creators across India." },
  { t: "", e: "Manufacturing", d: "Sublimation printing, heat fusing, precision cutting and couture stitching — all under one accountable roof." },
  { t: "", e: "Quality", d: "Every panel is colour-matched, inspected and hand-finished before it earns the Nayara name." },
  { t: "", e: "Technology", d: "Wide-format sublimation and calibrated colour workflows deliver vivid, wash-fast results, every time." },
  { t: "", e: "Infrastructure", d: "A modern atelier built for scale — from single studio drops to large event installations." },
];
const VALUES = [
  ["I", "Colour-true", "Calibrated artwork so every print matches the screen."],
  ["II", "Hand-finished", "Edges, sets and seams tailored with couture care."],
  ["III", "One roof", "Printed, fused, cut and stitched entirely in-house."],
  ["IV", "Any scale", "From a single studio drop to full event installations."],
];

const W1a = "M0 420 C 240 300, 480 520, 720 400 S 1200 280, 1440 380 L1440 520 C 1200 440, 960 600, 720 520 S 240 420, 0 560 Z";
const W1b = "M0 380 C 260 480, 520 300, 760 420 S 1180 520, 1440 340 L1440 500 C 1180 620, 940 420, 700 560 S 260 600, 0 520 Z";
const W2a = "M0 470 C 300 380, 560 600, 820 470 S 1240 360, 1440 450 L1440 490 C 1240 420, 1000 560, 800 510 S 300 430, 0 520 Z";
const W2b = "M0 500 C 280 600, 600 400, 860 500 S 1220 560, 1440 420 L1440 470 C 1220 600, 980 470, 820 540 S 280 640, 0 540 Z";
const THREAD = "M-20 120 C 160 20, 300 220, 480 120 S 800 20, 960 140 S 1260 230, 1460 90";

const CSS = `
@keyframes na-shim{0%{background-position:0% 50%}100%{background-position:200% 50%}}
.na-foil{background:${FOIL};background-size:200% 100%;-webkit-background-clip:text;background-clip:text;color:transparent;animation:na-shim 4s linear infinite}
.na-grain{opacity:.07;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")}
@keyframes na-rot{to{transform:rotate(360deg)}}
.na-seal{animation:na-rot 22s linear infinite}
@keyframes na-drip{to{top:56px}}
.na-drip:after{content:"";position:absolute;left:0;top:-30px;width:1px;height:30px;background:#E9D3A1;animation:na-drip 2s ease-in-out infinite}
@keyframes na-kb{from{transform:scale(1.12)}to{transform:scale(1.02)}}
.na-kb{animation:na-kb 14s ease-in-out infinite alternate}
.na-drop:first-letter{float:left;font-family:'Cormorant Garamond',Georgia,serif;font-size:92px;line-height:.8;padding:8px 14px 0 0;color:#9C7B4C}
.na-num{font-variant-numeric:lining-nums;font-feature-settings:"lnum";color:transparent;-webkit-text-stroke:1px #B89B72}
@media (prefers-reduced-motion:reduce){.na-foil,.na-seal,.na-kb,.na-drip:after{animation:none}}
`;

const Kick = ({ children, dark }) => (
  <div className={`flex items-center gap-4 text-[11px] uppercase tracking-[0.42em] ${dark ? "text-[#C9A96E]" : "text-[#9C7B4C]"}`}>
    <motion.i className="block h-px w-12 origin-left" style={{ background: `linear-gradient(90deg,transparent,${dark ? "#C9A96E" : "#9C7B4C"})` }}
      initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 1.2, ease: EASE }} />
    {children}
  </div>
);
const Up = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }} transition={{ duration: 1.1, delay, ease: EASE }}>{children}</motion.div>
);

export default function About() {
  const { isAdmin, src, reload } = useAdminImages();

  return (
    <div data-testid="about-page" className="overflow-hidden bg-[#F6F1E8] text-[#1A1410]">
      <style>{CSS}</style>

      {/* ── HERO: flowing silk ── */}
      <section className="relative min-h-[640px] overflow-hidden text-[#F6F1E8] lg:h-[820px]" style={{ background: "radial-gradient(ellipse at 70% 30%,#2a2018 0%,#14100C 60%)" }}>
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 820" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="naS1" x1="0" x2="1"><stop offset="0" stopColor="#8B6B3A" stopOpacity="0" /><stop offset=".3" stopColor="#C9A96E" stopOpacity=".55" /><stop offset=".55" stopColor="#F4E4BC" stopOpacity=".8" /><stop offset=".8" stopColor="#8B6B3A" stopOpacity=".5" /><stop offset="1" stopColor="#8B6B3A" stopOpacity="0" /></linearGradient>
            <linearGradient id="naS2" x1="0" x2="1"><stop offset="0" stopColor="#5a2a24" stopOpacity="0" /><stop offset=".4" stopColor="#7a3a30" stopOpacity=".7" /><stop offset=".7" stopColor="#b0654f" stopOpacity=".5" /><stop offset="1" stopColor="#5a2a24" stopOpacity="0" /></linearGradient>
          </defs>
          <g transform="translate(0,140)">
            <path fill="url(#naS2)" opacity=".75" d={W1a}><animate attributeName="d" dur="9s" repeatCount="indefinite" values={`${W1a};${W1b};${W1a}`} /></path>
            <path fill="url(#naS1)" opacity=".55" d={W2a}><animate attributeName="d" dur="7s" repeatCount="indefinite" values={`${W2a};${W2b};${W2a}`} /></path>
          </g>
        </svg>
        <div className="na-grain pointer-events-none absolute inset-0" />

        <div className="absolute right-[170px] top-[150px] hidden h-[170px] w-[170px] lg:block" aria-hidden="true">
          <svg className="na-seal absolute inset-0" viewBox="0 0 170 170">
            <defs><path id="naCp" d="M85 85 m-70 0 a70 70 0 1 1 140 0 a70 70 0 1 1 -140 0" /></defs>
            <text style={{ fontSize: 10.5, letterSpacing: 4.2, fill: "#C9A96E", textTransform: "uppercase" }}><textPath href="#naCp">Nayara Studio · Tiruppur · Est. · Atelier ·</textPath></text>
          </svg>
          <div className="absolute inset-[50px] grid place-items-center rounded-full border border-[#C9A96E]/50 text-[34px] italic text-[#E9D3A1]" style={SERIF}>N</div>
        </div>

        <div className="relative z-10 mx-auto max-w-[1440px] px-6 pb-28 pt-24 lg:px-[120px] lg:pt-[150px]">
          <Kick dark>Our Story</Kick>
          <h1 className="mt-8 text-[64px] font-light leading-[0.9] tracking-[-0.02em] sm:text-[100px] lg:text-[150px]" style={SERIF}>
            {["We turn fabric", "into"].map((l, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span className="inline-block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.4, delay: i * 0.18, ease: EASE }}>
                  {l}{i === 1 && <> <em className="na-foil pr-3">feeling.</em></>}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p className="mt-10 max-w-[470px] text-[15.5px] leading-[1.9] text-[#F6F1E8]/60"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.4, delay: 0.9, ease: EASE }}>
            Nayara Studio began with a simple belief — that a backdrop is never just cloth. It is the quiet stage on which portraits, weddings and interiors come alive.
          </motion.p>
        </div>
        <div className="absolute right-14 top-1/2 z-10 hidden origin-right -translate-y-1/2 rotate-90 text-[10px] uppercase tracking-[0.5em] text-[#C9A96E]/70 lg:block">Atelier of printed backdrops · Tiruppur</div>
        <div className="absolute bottom-14 left-6 z-10 flex items-center gap-3.5 text-[10px] uppercase tracking-[0.4em] text-[#F6F1E8]/50 lg:left-[120px]">
          <b className="na-drip relative block h-14 w-px overflow-hidden bg-[#C9A96E]/30" />Scroll
        </div>
      </section>

      {/* ── ATELIER: arch photo ── */}
      <section className="mx-auto grid max-w-[1440px] items-center gap-20 px-6 py-24 lg:grid-cols-[560px_1fr] lg:gap-[110px] lg:px-[120px] lg:py-[150px]">
        <div className="relative mx-auto h-[520px] w-full max-w-[560px] lg:h-[700px]">
          <div className="absolute -left-[22px] -top-[22px] bottom-[22px] right-[22px] -z-0 rounded-t-[300px] border border-[#C9A96E]" />
          <motion.div className="absolute inset-0 overflow-hidden rounded-t-[280px]"
            initial={{ clipPath: "inset(100% 0 0 0)" }} whileInView={{ clipPath: "inset(0% 0 0 0)" }} viewport={{ once: true }} transition={{ duration: 2, ease: EASE }}>
            <img src={src("aboutpg1", IMG.showroom)} alt="Nayara Studio" className="na-kb h-full w-full object-cover" style={{ objectPosition: "22% 50%" }} />
          </motion.div>
          {isAdmin && <ChangeBtn slot="aboutpg1" onDone={reload} />}
          <Up delay={0.6} className="absolute -right-4 bottom-[70px] w-[230px] bg-[#F6F1E8] px-6 py-5 shadow-[0_30px_60px_rgba(20,16,12,.12)] lg:-right-[60px]">
            <small className="text-[10px] uppercase tracking-[0.34em] text-[#9C7B4C]">Plate I</small>
            <p className="mt-1.5 text-[22px] italic leading-tight" style={SERIF}>A quiet stage, printed and finished by hand.</p>
          </Up>
        </div>

        <Up>
          <Kick>Our Promise</Kick>
          <h2 className="mt-6 text-5xl font-light leading-none sm:text-[76px]" style={SERIF}>Craft over<br /><em className="na-foil">compromise, always.</em></h2>
          <p className="na-drop mt-9 max-w-[520px] text-base leading-[1.95] text-[#5b5147]">
            Today we craft that stage for creators who refuse to compromise. From the first pixel of your artwork to the final fold before dispatch, a single studio stays accountable. That is how we keep colour true, seams clean and clients returning.
          </p>
          <div className="mt-9 flex items-center gap-5">
            <svg width="180" height="60" viewBox="0 0 180 60" aria-hidden="true">
              <motion.path d="M5 40 C 20 5, 35 5, 30 35 S 50 50, 60 25 S 75 20, 80 38 C 85 50, 100 15, 110 30 S 130 45, 140 22 S 165 30, 175 28"
                fill="none" stroke="#1A1410" strokeWidth="1.4" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 3, delay: 0.6, ease: "easeInOut" }} />
            </svg>
            <span className="text-[10px] uppercase tracking-[0.34em] text-[#9C7B4C]">The Nayara Atelier</span>
          </div>
        </Up>
      </section>

      {/* ── ONE GOLDEN THREAD: pillars ── */}
      <section className="relative bg-[#EFE7DA] px-6 py-24 lg:px-[120px] lg:py-[130px]">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-end justify-between gap-8">
          <Up><Kick>One golden thread</Kick>
            <h2 className="mt-6 text-5xl font-light leading-none sm:text-[64px]" style={SERIF}>Five threads,<br />one <em className="text-[#9C7B4C]">studio</em></h2></Up>
          <Up delay={0.2}><p className="max-w-[330px] text-sm leading-[1.8] text-[#6b6157]">Everything that makes a Nayara fabric — woven into one accountable house, from idea to finished stage.</p></Up>
        </div>
        <svg className="pointer-events-none absolute left-0 right-0 top-[300px] hidden h-[150px] w-full lg:block" viewBox="0 0 1440 240" preserveAspectRatio="none" aria-hidden="true">
          <defs><linearGradient id="naGold" x1="0" x2="1"><stop offset="0" stopColor="#8B6B3A" /><stop offset=".5" stopColor="#F4E4BC" /><stop offset="1" stopColor="#8B6B3A" /></linearGradient></defs>
          <motion.path d={THREAD} fill="none" stroke="url(#naGold)" strokeWidth="2" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 4, ease: [0.65, 0, 0.35, 1] }} />
          <circle r="5" fill="#E9D3A1"><animateMotion dur="6s" repeatCount="indefinite" path={THREAD} /></circle>
        </svg>
        <div className="relative mx-auto mt-16 grid max-w-[1200px] gap-12 sm:grid-cols-2 lg:mt-[190px] lg:grid-cols-5 lg:gap-10">
          {PILLARS.map((p, i) => (
            <Up key={p.e} delay={i * 0.15} className={i % 2 ? "lg:mt-[70px]" : ""}>
              <div className="na-num text-[110px] font-light leading-[0.8]" style={SERIF}>0{i + 1}</div>
              <h3 className="mt-4 text-[30px] font-light leading-tight" style={SERIF}>{p.t && `${p.t} `}<em className="text-[#9C7B4C]">{p.e}</em></h3>
              <p className="mt-3 text-[14px] leading-[1.85] text-[#5b5147]">{p.d}</p>
              <div className="mt-6 h-px bg-gradient-to-r from-[#C9A96E] to-transparent" />
            </Up>
          ))}
        </div>
      </section>

      {/* ── FABRIC · FEELING · FINISH (static, fades in) ── */}
      <section className="flex flex-wrap items-center justify-center gap-x-10 gap-y-2 bg-[#14100C] px-6 py-14 lg:gap-x-16">
        {["Fabric", "Feeling", "Finish"].map((w, i) => (
          <React.Fragment key={w}>
            {i > 0 && <motion.span className="h-2 w-2 rotate-45 bg-[#C9A96E]" initial={{ opacity: 0, scale: 0 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 + i * 0.35 }} />}
            <motion.span className={`text-6xl font-light leading-none sm:text-[110px] ${i === 1 ? "na-foil italic" : ""}`}
              style={i === 1 ? SERIF : { ...SERIF, color: "transparent", WebkitTextStroke: "1px rgba(201,169,110,.65)" }}
              initial={{ opacity: 0, filter: "blur(8px)" }} whileInView={{ opacity: 1, filter: "blur(0px)" }} viewport={{ once: true }} transition={{ duration: 1.2, delay: i * 0.35, ease: EASE }}>{w}</motion.span>
          </React.Fragment>
        ))}
      </section>

      {/* ── VALUES ── */}
      <section className="mx-auto grid max-w-[1440px] px-6 pt-24 sm:grid-cols-2 lg:grid-cols-4 lg:px-[120px] lg:pt-[120px]">
        {VALUES.map(([n, t, d], i) => (
          <Up key={n} delay={i * 0.12} className={`group relative border-l border-[#DCCFB8] px-8 pb-10 pt-8 transition-colors duration-500 hover:bg-[#14100C] ${i === 3 ? "lg:border-r" : ""}`}>
            <motion.span className="absolute -left-px top-0 w-px origin-top bg-[#9C7B4C]" style={{ height: "100%" }} initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, delay: i * 0.15, ease: EASE }} />
            <small className="text-[10px] tracking-[0.34em] text-[#9C7B4C] group-hover:text-[#C9A96E]">{n}</small>
            <h4 className="mt-11 text-[32px] font-light leading-tight transition-colors group-hover:text-[#F6F1E8]" style={SERIF}>{t}</h4>
            <p className="mt-3 text-[13.5px] leading-[1.8] text-[#6b6157] transition-colors group-hover:text-[#F6F1E8]/60">{d}</p>
          </Up>
        ))}
      </section>

      {/* ── QUOTE ── */}
      <section className="px-6 pb-28 pt-24 lg:px-[120px] lg:pt-[130px]">
        <div className="relative grid h-[460px] place-items-center overflow-hidden text-center text-[#F6F1E8] lg:h-[520px]">
          <img src={src("aboutpg2", IMG.fashionSheer)} alt="" className="na-kb absolute inset-0 h-full w-full object-cover" style={{ filter: "brightness(.35) saturate(.8)" }} />
          {isAdmin && <ChangeBtn slot="aboutpg2" onDone={reload} />}
          <div className="pointer-events-none absolute inset-6 border border-[#C9A96E]/40" />
          <Up className="relative max-w-[900px] px-6">
            <p className="text-4xl font-light italic leading-[1.15] sm:text-[58px]" style={SERIF}>“We don't just print fabric — we set the stage for the moments people remember.”</p>
            <div className="mt-8 text-[10px] uppercase tracking-[0.42em] text-[#C9A96E]">— Nayara Studio</div>
          </Up>
        </div>
      </section>
    </div>
  );
}
